import React, { useEffect, useRef, useState } from 'react';
import { X, Send, Sparkles, Loader2, FileText } from 'lucide-react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { useTheme } from '../../../context/ThemeContext';
import { modelServicePost } from '../../../utils/modelService';

const renderer = new marked.Renderer();
const originalLinkRenderer = renderer.link.bind(renderer);
renderer.link = function (href, title, text) {
  const html = originalLinkRenderer(href, title, text);
  return html.replace(/^<a /, '<a target="_blank" rel="noopener noreferrer" ');
};

function formatMarkdown(text) {
  if (typeof text !== 'string' || !text) return '';
  try {
    const rawHTML = marked(text, { gfm: true, breaks: true, renderer });
    return DOMPurify.sanitize(rawHTML, {
      ADD_ATTR: ['target', 'rel'],
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a', 'hr', 'del',
        'table', 'thead', 'tbody', 'tr', 'td', 'th', 'span', 'div', 'sup', 'sub',
      ],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    });
  } catch {
    return text.replace(/\n/g, '<br>');
  }
}

const SUGGESTED_PROMPTS = [
  { icon: '📝', label: 'Summarize this document', message: 'Give me a concise summary of this document.' },
  { icon: '📅', label: 'Key dates', message: 'What are the important dates mentioned in this document?' },
  { icon: '👥', label: 'People & parties', message: 'Who are the people, parties, or organizations mentioned?' },
  { icon: '⚡', label: 'Action required', message: 'What actions, if any, does this document require?' },
];

export default function AskJproDrawer({ isOpen, onClose, extractionId, filename, documentType }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setMessages([]);
    setInput('');
    setError(null);
  }, [extractionId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const sendQuestion = async (question) => {
    const trimmed = (question || '').trim();
    if (!trimmed || loading || !extractionId) return;

    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    setLoading(true);
    setError(null);

    try {
      const res = await modelServicePost('/api/ask-document', {
        extraction_id: extractionId,
        question: trimmed,
        conversation_history: history,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Request failed (${res.status})`);
      }

      const data = await res.json();
      setMessages((prev) => [...prev, { role: 'assistant', content: data.answer || '(empty response)' }]);
    } catch (e) {
      setError(e.message || 'Failed to get an answer');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = (e) => {
    e?.preventDefault?.();
    sendQuestion(input);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendQuestion(input);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 z-40 flex">
      <div className="fixed inset-0 bg-black/20" onClick={onClose} />

      <div className={`relative ml-auto w-full max-w-md flex flex-col shadow-xl ${
        isDark ? 'bg-gray-900' : 'bg-white'
      }`}>
        <div className={`flex items-center justify-between px-4 py-3 border-b shrink-0 ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles size={18} className="text-blue-500 shrink-0" />
            <div className="min-w-0">
              <h3 className={`font-unna text-lg font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Ask JPro
              </h3>
              <div className={`flex items-center gap-1.5 font-rasa text-sm truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                <FileText size={12} className="shrink-0" />
                <span className="truncate">{filename}</span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {documentType && (
          <div className={`px-4 py-2 border-b shrink-0 ${isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex items-center gap-2">
              <span className={`font-rasa text-xs uppercase tracking-wide font-medium ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Document
              </span>
              <span className={`font-rasa text-sm font-medium truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {documentType}
              </span>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {messages.length === 0 && !loading ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <Sparkles size={40} className={`mb-3 ${isDark ? 'text-gray-700' : 'text-gray-300'}`} />
              <h4 className={`font-unna text-base font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Ask about this document
              </h4>
              <p className={`font-rasa text-sm max-w-xs mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                Chat is session-only and resets when you refresh the page.
              </p>
              <div className="w-full max-w-xs space-y-2">
                {SUGGESTED_PROMPTS.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => sendQuestion(prompt.message)}
                    disabled={loading || !extractionId}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left font-rasa text-sm transition-colors ${
                      isDark
                        ? 'bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700'
                        : 'bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200'
                    } disabled:opacity-50`}
                  >
                    <span className="text-lg shrink-0">{prompt.icon}</span>
                    <span className="font-medium">{prompt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-xl px-3 py-2 font-rasa text-base ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : isDark ? 'bg-gray-800 text-gray-200' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {msg.role === 'user' ? (
                      <div className="whitespace-pre-wrap break-words">{msg.content}</div>
                    ) : (
                      <div
                        className={`prose max-w-none break-words ${isDark ? 'prose-invert' : ''} prose-headings:font-unna prose-headings:mt-2 prose-headings:mb-1 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-hr:my-2 prose-pre:my-1.5 prose-blockquote:my-1.5`}
                        dangerouslySetInnerHTML={{ __html: formatMarkdown(msg.content) }}
                      />
                    )}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start">
                  <div className={`rounded-xl px-3 py-2 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                    <Loader2 size={16} className="animate-spin text-blue-500" />
                  </div>
                </div>
              )}

              {error && (
                <div className={`rounded-xl px-3 py-2 font-rasa text-base border ${
                  isDark ? 'bg-red-900/20 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'
                }`}>
                  {error}
                </div>
              )}

              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        <div className={`px-4 py-3 border-t shrink-0 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <form onSubmit={handleSend} className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about this document..."
              disabled={loading || !extractionId}
              className={`flex-1 px-3 py-2 rounded-lg border font-rasa text-sm ${
                isDark
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 [color-scheme:dark]'
                  : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 [color-scheme:light]'
              } disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
            />
            <button
              type="submit"
              disabled={!input.trim() || loading || !extractionId}
              className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:hover:bg-blue-600 transition-colors"
              aria-label="Send"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
